import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import MainContext from "./Context/MainContext/MainContext";
import Setting from "./Utility/Setting";

import MUIBox from "@mui/material/Box";
import MUIContainer from "@mui/material/Container";
import { getPagesConfig } from "./Component/AppBar";
import DialogNavigation from "./Component/Dialog/DialogNavigation";

/**
 * Renders the header section for the layout. Override by passing renderHeader
 * prop.
 * @returns {React.ReactNode|null}
 */
function defaultRenderHeader() {
    return null;
}

/**
 * Renders the main content section for the layout. Override by passing
 * renderContent prop.
 * @returns {React.ReactNode|null}
 */
function defaultRenderContent() {
    return null;
}

/**
 * Returns the maximum width for the container. Override by passing getMaxWidth
 * prop.
 * @returns {number}
 */
function defaultGetMaxWidth() {
    return 9999;
}

/**
 * Returns the background color for the theme. Override by passing
 * getBackgroundColor prop.
 * @param {object} theme - The MUI theme object.
 * @returns {string}
 */
function defaultGetBackgroundColor(theme) {
    return theme.palette.background.default;
}

/**
 * Returns the default loader object. Override by passing loader prop.
 * @returns {object|null}
 */
function defaultGetLoader() {
    return null;
}

/**
 * Main Layer component providing layout, theming, and session/context logic.
 * @param {object} props
 * @param {function} [props.renderHeader] - Function to render header section
 * @param {function} [props.renderContent] - Function to render content section
 * @param {function} [props.getMaxWidth] - Function to get container max width
 * @param {function} [props.getBackgroundColor] - Function to get background
 * color
 * @param {object} [props.loader] - Optional custom loader object
 * @param {boolean} [props.passthrough] - When true, skip visual shell wrappers
 * and render only the provided content (plus shared navigation dialog).
 * @returns {React.ReactElement|null}
 */
export default function LayerFunctional({
    renderHeader = defaultRenderHeader,
    renderContent = defaultRenderContent,
    getMaxWidth = defaultGetMaxWidth,
    getBackgroundColor = defaultGetBackgroundColor,
    loader = defaultGetLoader(),
    passthrough = false,
}) {
    const context = useContext(MainContext);
    const [mounted, setMounted] = useState(false);
    const history = useHistory();

    useEffect(() => handleCredentials(), []);
    useEffect(() => handleDocumentTitle(), []);
    useEffect(() => handleMount(context.loaded, setMounted), [context.loaded]);
    useEffect(() => handleRedirect(context.loaded, history, loader), [context.loaded, history, loader]);

    // Require the layer be mounted (fully loaded etc) before rendering anything.
    if (!mounted) {
        return null;
    }

    // Passthrough mode keeps auth/mount/title/redirect behavior from this layer
    // utility but skips all visual wrappers so child content can render its own shell.
    if (passthrough === true) {
        return (
            <>
                {renderContent()}
                <DialogNavigation
                    open={Boolean(context.navigationOpen)}
                    onClose={() => context.setContextItem("navigationOpen", false)}
                    pages={getPagesConfig({}, context)}
                />
            </>
        );
    }

    return (
        <>
            <MUIBox
                sx={(theme) => ({
                    backgroundColor: getBackgroundColor(theme),
                    pt: 2,
                    pb: 2,
                    pl: 2,
                    pr: 2,
                    minHeight: "calc(100vh - 50px)",
                })}
            >
                {/* Setting maxWidth={false} on MUIContainer removes MUI's built-in max width constraints,
                    allowing our custom getMaxWidth() function to fully control the container width */}
                <MUIContainer
                    disableGutters
                    maxWidth={false}
                    sx={{
                        maxWidth: getMaxWidth(),
                    }}
                >
                    {renderHeader()}
                    {renderContent()}
                </MUIContainer>
            </MUIBox>
            <DialogNavigation
                open={Boolean(context.navigationOpen)}
                onClose={() => context.setContextItem("navigationOpen", false)}
                pages={getPagesConfig({}, context)}
            />
        </>
    );
}

/**
 * Transfers credentials from localStorage to sessionStorage if needed. Used to
 * ensure session credentials are available after login.
 */
function handleCredentials() {
    const url = new URL(window.location.href);
    if (
        (!Setting.get("session", true, window.sessionStorage) ||
            !Setting.get("organization", true, window.sessionStorage) ||
            !Setting.get("userprofile", true, window.sessionStorage)) &&
        (Setting.get("session", true, window.localStorage) &&
            Setting.get("organization", true, window.localStorage) &&
            Setting.get("userprofile", true, window.localStorage)) &&
        !["/authorize", "/"].includes(url.pathname)
    ) {
        Setting.set("session", Setting.get("session", true, window.localStorage), window.sessionStorage);
        Setting.set("organization", Setting.get("organization", true, window.localStorage), window.sessionStorage);
        Setting.set("userprofile", Setting.get("userprofile", true, window.localStorage), window.sessionStorage);
    }
}

/**
 * Sets the document title based on the current route and organization. Uses
 * organization setting to display company name in the title.
 */
function handleDocumentTitle() {
    const url = new URL(window.location.href);
    if (url.pathname === "/authorize" || url.pathname === "/") {
        document.title = "Sales Pilot";
    } else {
        let displayedCompany;
        switch (Setting.get("organization")) {
            case "rehabmedical": displayedCompany = "Rehab Medical"; break;
            case "corkmedical": displayedCompany = "Cork Medical"; break;
            case "uat": displayedCompany = "UAT"; break;
            case "localhost": displayedCompany = "UAT [localhost]"; break;
            default: displayedCompany = Setting.get("organization"); break;
        }
        document.title = `Sales Pilot${displayedCompany ? ` • ${displayedCompany}` : ""}`;
    }
}

/**
 * Handles logic after mount, including scroll-to-top and setting mounted state.
 * Used to ensure layout is only rendered when context is loaded or on certain
 * routes.
 * @param {boolean} contextLoaded - Whether context is loaded.
 * @param {function} setMounted - State setter for mounted.
 */
function handleMount(contextLoaded, setMounted) {
    const url = new URL(window.location.href);
    if (contextLoaded === true || ["/authorize", "/", "/load", "/logout"].includes(url.pathname)) {
        setMounted(true);
        window.scrollTo(0, 0);
    }
}

/**
 * Handles redirect logic based on session and context state. Redirects to "/"
 * if session is missing, or to "/load" if context is not loaded.
 * @param {boolean} contextLoaded - Whether context is loaded.
 * @param {object} history - React Router history object.
 * @param {object} loader - Optional custom loader object.
 */
function handleRedirect(contextLoaded, history, loader) {
    const url = new URL(window.location.href);
    if (!Setting.get("session") && !["/authorize", "/", "/load"].includes(url.pathname)) {
        history.replace({ pathname: "/", state: { from: url.pathname } });
    } else if (!contextLoaded && !["/authorize", "/", "/load", "/logout"].includes(url.pathname)) {
        history.replace({ pathname: "/load", state: { from: url.pathname, loader: loader } });
    }
}

